// src/pages/GiftifyHome.jsx
import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../../redux/actions/authActions";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faGift, 
  faHeart, 
  faShoppingCart, 
  faStar, 
  faTags, 
  faRocket, 
  faUsers, 
  faChartLine, 
  faShieldAlt, 
  faNewspaper, 
  faTrophy, 
  faGamepad, 
  faRobot,
  faSearch,
  faFilter,
  faUser,
  faSignOutAlt,
  faCheckCircle,
  faTimesCircle,
  faMinus,
  faPlus,
  faEdit,
  faTrash
} from '@fortawesome/free-solid-svg-icons';

const GiftifyHome = () => {
  const authState = useSelector((state) => state.authReducer || {});
  const isLoggedIn = authState?.isLoggedIn || false;
  const userName = authState?.user?.name || "Gift Lover";

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [cart, setCart] = useState([]);
  const [wishlist, setWishlist] = useState([]);
  const [activeTab, setActiveTab] = useState("All Gifts");
  const [searchQuery, setSearchQuery] = useState("");
  const [priceFilter, setPriceFilter] = useState("all");
  const [ageFilter, setAgeFilter] = useState("all");
  const [sortBy, setSortBy] = useState("popularity");
  const [couponCode, setCouponCode] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [shippingAddress, setShippingAddress] = useState("");

  // Load cart and wishlist from localStorage on mount
  useEffect(() => {
    const savedCart = localStorage.getItem('giftifyCart');
    const savedWishlist = localStorage.getItem('giftifyWishlist');
    if (savedCart) setCart(JSON.parse(savedCart));
    if (savedWishlist) setWishlist(JSON.parse(savedWishlist));
  }, []);

  // Save to localStorage whenever cart/wishlist changes
  useEffect(() => {
    localStorage.setItem('giftifyCart', JSON.stringify(cart));
    localStorage.setItem('giftifyWishlist', JSON.stringify(wishlist));
  }, [cart, wishlist]);

  // Dummy gift data (expanded slightly)
  const dummyGifts = [
    // Electronics
    {
      id: 1,
      name: "Wireless Earbuds Pro",
      category: "Electronics",
      price: 49.99,
      image: "https://via.placeholder.com/300x300/FF6B6B/FFFFFF?text=Earbuds",
      description: "High-quality noise-cancelling earbuds for music lovers.",
      popularity: 4.9,
      ageGroup: "Adults",
      isNew: false,
      isPersonalized: false,
      isInnovative: true,
      stock: 50
    },
    {
      id: 2,
      name: "Smart Watch Elite",
      category: "Electronics",
      price: 199.99,
      image: "https://via.placeholder.com/300x300/4ECDC4/FFFFFF?text=Watch",
      description: "Track your fitness with this sleek smartwatch.",
      popularity: 4.7,
      ageGroup: "Adults",
      isNew: true,
      isPersonalized: false,
      isInnovative: true,
      stock: 30
    },
    // Books
    {
      id: 3,
      name: "The Art of Gifting",
      category: "Books",
      price: 19.99,
      image: "https://via.placeholder.com/300x300/45B7D1/FFFFFF?text=Book",
      description: "A guide to thoughtful gift selection.",
      popularity: 4.8,
      ageGroup: "Adults",
      isNew: false,
      isPersonalized: true,
      isInnovative: false,
      stock: 100
    },
    {
      id: 4,
      name: "Children's Adventure Tales",
      category: "Books",
      price: 12.99,
      image: "https://via.placeholder.com/300x300/96CEB4/FFFFFF?text=Kids+Book",
      description: "Engaging stories for young explorers.",
      popularity: 4.6,
      ageGroup: "Kids",
      isNew: true,
      isPersonalized: false,
      isInnovative: false,
      stock: 75
    },
    // Home & Kitchen
    {
      id: 5,
      name: "Personalized Mug Set",
      category: "Home & Kitchen",
      price: 24.99,
      image: "https://via.placeholder.com/300x300/F7DC6F/000000?text=Mug",
      description: "Custom mugs for coffee enthusiasts.",
      popularity: 4.9,
      ageGroup: "Adults",
      isNew: false,
      isPersonalized: true,
      isInnovative: false,
      stock: 40
    },
    {
      id: 6,
      name: "Eco-Friendly Bamboo Set",
      category: "Home & Kitchen",
      price: 34.99,
      image: "https://via.placeholder.com/300x300/27AE60/FFFFFF?text=Bamboo",
      description: "Sustainable kitchen essentials.",
      popularity: 4.5,
      ageGroup: "Adults",
      isNew: true,
      isPersonalized: false,
      isInnovative: true,
      stock: 60
    },
    // Fashion
    {
      id: 7,
      name: "Silk Scarf Collection",
      category: "Fashion",
      price: 59.99,
      image: "https://via.placeholder.com/300x300/E74C3C/FFFFFF?text=Scarf",
      description: "Elegant scarves for any occasion.",
      popularity: 4.7,
      ageGroup: "Adults",
      isNew: false,
      isPersonalized: true,
      isInnovative: false,
      stock: 25
    },
    {
      id: 8,
      name: "Kids' Graphic Tees",
      category: "Fashion",
      price: 15.99,
      image: "https://via.placeholder.com/300x300/3498DB/FFFFFF?text=Tees",
      description: "Fun tees for children.",
      popularity: 4.4,
      ageGroup: "Kids",
      isNew: true,
      isPersonalized: false,
      isInnovative: false,
      stock: 80
    },
    // Toys & Games
    {
      id: 9,
      name: "Puzzle Master Set",
      category: "Toys & Games",
      price: 29.99,
      image: "https://via.placeholder.com/300x300/9B59B6/FFFFFF?text=Puzzle",
      description: "Challenging puzzles for all ages.",
      popularity: 4.8,
      ageGroup: "Kids",
      isNew: false,
      isPersonalized: false,
      isInnovative: true,
      stock: 35
    },
    {
      id: 10,
      name: "Board Game Night",
      category: "Toys & Games",
      price: 39.99,
      image: "https://via.placeholder.com/300x300/2ECC71/000000?text=Board+Game",
      description: "Family board game for evenings.",
      popularity: 4.9,
      ageGroup: "Family",
      isNew: true,
      isPersonalized: true,
      isInnovative: false,
      stock: 20
    },
    // Corporate Gifts
    {
      id: 11,
      name: "Branded Notebook",
      category: "Corporate",
      price: 14.99,
      image: "https://via.placeholder.com/300x300/34495E/FFFFFF?text=Notebook",
      description: "Professional notebooks for teams.",
      popularity: 4.6,
      ageGroup: "Adults",
      isNew: false,
      isPersonalized: true,
      isInnovative: false,
      stock: 150
    },
    {
      id: 12,
      name: "Tech Gadget Kit",
      category: "Corporate",
      price: 89.99,
      image: "https://via.placeholder.com/300x300/E67E22/FFFFFF?text=Gadget+Kit",
      description: "Innovative kit for corporate events.",
      popularity: 4.7,
      ageGroup: "Adults",
      isNew: true,
      isPersonalized: false,
      isInnovative: true,
      stock: 45
    },
    // Bulk Gifts
    {
      id: 13,
      name: "Chocolate Box Bulk (10pc)",
      category: "Bulk",
      price: 99.99,
      image: "https://via.placeholder.com/300x300/8E44AD/FFFFFF?text=Chocolates",
      description: "Bulk chocolates for events.",
      popularity: 4.8,
      ageGroup: "All",
      isNew: false,
      isPersonalized: false,
      isInnovative: false,
      stock: 10
    },
    {
      id: 14,
      name: "Return Gift Hamper",
      category: "Return Gifts",
      price: 7.99,
      image: "https://via.placeholder.com/300x300/F39C12/FFFFFF?text=Hamper",
      description: "Affordable return gifts for parties.",
      popularity: 4.5,
      ageGroup: "Kids",
      isNew: true,
      isPersonalized: true,
      isInnovative: false,
      stock: 200
    },
    // Personalized
    {
      id: 15,
      name: "Custom Photo Frame",
      category: "Personalized",
      price: 22.99,
      image: "https://via.placeholder.com/300x300/D35400/FFFFFF?text=Frame",
      description: "Personalized frames for memories.",
      popularity: 4.9,
      ageGroup: "Adults",
      isNew: false,
      isPersonalized: true,
      isInnovative: false,
      stock: 55
    },
    // Innovative
    {
      id: 16,
      name: "Smart Plant Pot",
      category: "Innovative",
      price: 44.99,
      image: "https://via.placeholder.com/300x300/1ABC9C/FFFFFF?text=Plant+Pot",
      description: "App-controlled plant grower.",
      popularity: 4.6,
      ageGroup: "Adults",
      isNew: true,
      isPersonalized: false,
      isInnovative: true,
      stock: 28
    },
    // More items
    {
      id: 17,
      name: "Jewelry Box",
      category: "Fashion",
      price: 79.99,
      image: "https://via.placeholder.com/300x300/C0392B/FFFFFF?text=Jewelry",
      description: "Elegant jewelry storage.",
      popularity: 4.7,
      ageGroup: "Adults",
      isNew: false,
      isPersonalized: true,
      isInnovative: false,
      stock: 32
    },
    {
      id: 18,
      name: "Drone Mini",
      category: "Electronics",
      price: 129.99,
      image: "https://via.placeholder.com/300x300/7F8C8D/FFFFFF?text=Drone",
      description: "Beginner drone for fun flights.",
      popularity: 4.4,
      ageGroup: "Teens",
      isNew: true,
      isPersonalized: false,
      isInnovative: true,
      stock: 15
    },
    {
      id: 19,
      name: "Scented Candle Set",
      category: "Home & Kitchen",
      price: 18.99,
      image: "https://via.placeholder.com/300x300/85C1E9/000000?text=Candle",
      description: "Relaxing aromatherapy candles.",
      popularity: 4.8,
      ageGroup: "Adults",
      isNew: false,
      isPersonalized: true,
      isInnovative: false,
      stock: 70
    },
    {
      id: 20,
      name: "LEGO Creator Kit",
      category: "Toys & Games",
      price: 54.99,
      image: "https://via.placeholder.com/300x300/E91E63/FFFFFF?text=LEGO",
      description: "Creative building kit for kids.",
      popularity: 4.9,
      ageGroup: "Kids",
      isNew: true,
      isPersonalized: false,
      isInnovative: false,
      stock: 40
    }
  ];

  // Filter and sort gifts
  const filteredGifts = dummyGifts
    .filter(gift => {
      const matchesSearch = gift.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           gift.description.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesPrice = priceFilter === "all" || 
                           (priceFilter === "under20" && gift.price < 20) ||
                           (priceFilter === "20-50" && gift.price >= 20 && gift.price <= 50) ||
                           (priceFilter === "over50" && gift.price > 50);
      const matchesAge = ageFilter === "all" || gift.ageGroup === ageFilter;
      const matchesTab = activeTab === "All Gifts" || 
                         (activeTab === "New Arrivals" && gift.isNew) ||
                         (activeTab === "Personalized" && gift.isPersonalized) ||
                         (activeTab === "Innovative" && gift.isInnovative) ||
                         (activeTab === "Popular Picks" && gift.popularity > 4.5) ||
                         (activeTab === "By Category" ? true : gift.category === activeTab) ||
                         (activeTab === "By Age" ? true : gift.ageGroup === activeTab) ||
                         (activeTab === "Corporate" && (gift.category === "Corporate" || gift.category === "Bulk")) ||
                         (activeTab === "Bulk & Returns" && (gift.category === "Bulk" || gift.category === "Return Gifts"));
      return matchesSearch && matchesPrice && matchesAge && matchesTab;
    })
    .sort((a, b) => {
      if (sortBy === "price") return a.price - b.price;
      if (sortBy === "new") return b.isNew - a.isNew;
      return b.popularity - a.popularity;
    });

  const handleLogout = () => {
    dispatch(logout());
    navigate("/"); 
  };

  const addToCart = (gift, wrap = false) => {
    const existing = cart.find(item => item.id === gift.id);
    if (existing) {
      setCart(cart.map(item => item.id === gift.id 
        ? { ...item, quantity: item.quantity + 1, giftWrap: item.giftWrap || wrap } 
        : item));
    } else {
      setCart([...cart, { ...gift, quantity: 1, giftWrap: wrap }]);
    }
  };

  const toggleWishlist = (gift) => {
    const existing = wishlist.find(item => item.id === gift.id);
    if (existing) {
      setWishlist(wishlist.filter(item => item.id !== gift.id));
    } else {
      setWishlist([...wishlist, gift]);
    }
  };

  const removeFromCart = (id) => {
    setCart(cart.filter(item => item.id !== id));
  };

  const updateQuantity = (id, quantity) => {
    if (quantity <= 0) {
      removeFromCart(id);
    } else {
      setCart(cart.map(item => item.id === id ? { ...item, quantity } : item));
    }
  };

  const toggleGiftWrap = (id) => {
    setCart(cart.map(item => item.id === id ? { ...item, giftWrap: !item.giftWrap } : item));
  };

  const applyCoupon = () => {
    // Dummy coupons: "SAVE10" for 10% off, "FREEWRAP" for free wrapping
    if (couponCode === "SAVE10") {
      setAppliedCoupon({ code: "SAVE10", discount: 0.10 });
    } else if (couponCode === "FREEWRAP") {
      setCart(cart.map(item => ({ ...item, giftWrap: true })));
      setAppliedCoupon({ code: "FREEWRAP", discount: 0 });
    } else {
      alert("Invalid coupon code!");
      return;
    }
    setCouponCode("");
  };

  const clearCoupon = () => {
    setAppliedCoupon(null);
  };

  const totalItems = cart.reduce((sum, item) => sum + item.quantity, 0);
  const subtotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const wrappingCost = cart.reduce((sum, item) => sum + (item.giftWrap ? 5 * item.quantity : 0), 0);
  const discount = appliedCoupon ? (appliedCoupon.discount * subtotal) : 0;
  const tax = (subtotal - discount + wrappingCost) * 0.08; // 8% dummy tax
  const shipping = shippingAddress ? 5.99 : 0; // Fixed $5.99 if addressed
  const total = subtotal + wrappingCost + tax + shipping - discount;

  // Group names
  const groupNames = [
    { label: "All Gifts", icon: faGift, description: "Browse all categories" },
    { label: "By Category", icon: faTags, description: "Electronics, Books, Fashion & more" },
    { label: "By Price", icon: faChartLine, description: "Budget-friendly to luxury" },
    { label: "New Arrivals", icon: faRocket, description: "Latest gift launches" },
    { label: "Personalized", icon: faHeart, description: "Custom & unique touches" },
    { label: "Innovative", icon: faRobot, description: "Cutting-edge & techy gifts" },
    { label: "Popular Picks", icon: faStar, description: "Top-rated by customers" },
    { label: "By Age", icon: faUsers, description: "Gifts for Kids, Adults, Family" },
    { label: "Corporate", icon: faShieldAlt, description: "Bulk & professional gifting" },
    { label: "Bulk & Returns", icon: faTrophy, description: "Event & return gifts" },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-gray-900 via-black to-rose-900 text-gray-100 font-sans overflow-x-hidden">
      {/* Enhanced header with dark gift theme */}
      <header className="sticky top-0 z-50 bg-gray-800/80 backdrop-blur-md border-b border-rose-800/30 px-4 py-3 flex items-center justify-between shadow-lg">
        <div className="flex items-center gap-2">
          <div className="text-2xl font-bold text-rose-400 drop-shadow-lg">🎁 Giftify</div>
          <div className="text-xs text-gray-300 hidden sm:block">Your One-Stop Gifting Hub</div>
        </div>

        <div className="hidden md:flex items-center gap-4">
          {isLoggedIn ? (
            <>
              <div className="text-sm text-rose-300 flex items-center gap-1">
                <FontAwesomeIcon icon={faUser} /> {userName}
              </div>
              <button
                onClick={() => setIsCartOpen(!isCartOpen)}
                className="relative p-2 rounded-full bg-gray-700/50 hover:bg-rose-700/70 transition-all"
                title="Shopping Cart"
              >
                <FontAwesomeIcon icon={faShoppingCart} className="text-rose-400" />
                {totalItems > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                    {totalItems}
                  </span>
                )}
              </button>
              <button
                onClick={handleLogout}
                className="p-2 rounded-full bg-red-600/80 hover:bg-red-700/90 transition-all"
                title="Logout"
              >
                <FontAwesomeIcon icon={faSignOutAlt} />
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => navigate("/login")}
                className="px-4 py-2 rounded-full border border-rose-500/50 text-sm text-rose-300 hover:bg-rose-900/50 transition backdrop-blur-sm"
              >
                Login
              </button>
              <button
                onClick={() => navigate("/signup")}
                className="px-4 py-2 rounded-full bg-rose-600/80 text-gray-100 text-sm hover:bg-rose-500/90 transition backdrop-blur-sm font-medium"
              >
                Sign Up
              </button>
            </>
          )}
        </div>

        {/* Mobile hamburger */}
        <button
          onClick={() => setIsMobileMenuOpen((s) => !s)}
          className="md:hidden w-10 h-10 flex items-center justify-center text-xl text-rose-400 rounded-full bg-gray-700/50 hover:bg-rose-700/30 transition"
          aria-label="Toggle menu"
        >
          {isMobileMenuOpen ? "✕" : "☰"}
        </button>
      </header>

      {/* Mobile menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-gray-800/95 border-b border-rose-800/30 px-4 py-4 backdrop-blur-md">
          {isLoggedIn ? (
            <div className="flex flex-col items-center gap-3 mb-4">
              <div className="text-rose-400 font-medium text-lg">Welcome, {userName} 🎁</div>
              <div className="flex gap-3">
                <button
                  onClick={() => setIsCartOpen(!isCartOpen)}
                  className="p-3 rounded-full bg-gray-700/50 hover:bg-rose-700/70 transition"
                >
                  <FontAwesomeIcon icon={faShoppingCart} className="text-rose-400" />
                </button>
                <button
                  onClick={handleLogout}
                  className="p-3 rounded-full bg-red-600/80 hover:bg-red-700/90 transition"
                >
                  <FontAwesomeIcon icon={faSignOutAlt} />
                </button>
              </div>
            </div>
          ) : (
            <div className="flex justify-center gap-6 mb-4">
              <Link to="/login" className="text-rose-400 font-medium">Login</Link>
              <Link to="/signup" className="text-rose-400 font-medium">Sign Up</Link>
            </div>
          )}

          <div className="flex flex-wrap justify-center gap-4 text-sm text-gray-300">
            <Link to="/about" className="hover:text-rose-400 transition">About Giftify</Link>
            <Link to="/faq" className="hover:text-rose-400 transition">FAQ</Link>
            <Link to="/contact" className="hover:text-rose-400 transition">Contact</Link>
          </div>
        </div>
      )}

      {/* Hero Banner - Dark */}
      <section className="relative bg-gradient-to-r from-rose-900 to-black text-gray-100 py-6 px-4 overflow-hidden">
        <div className="mx-auto text-center relative z-10">
          <h1 className="text-m lg:text-xl font-bold mb-2">Discover Joyful Gifting Powered by Think5Whys</h1>
          <p className="text-sm lg:text-m mb-8 max-w-2xl mx-auto text-gray-300">Find the perfect gift for any occasion – personalized, innovative, and delivered with love. Single, bulk, corporate – we've got you covered!</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 place-items-center w-full">
            <Link
                to="/gifts"
                className="px-2 py-3 bg-rose-600 text-gray-100 rounded-lg font-semibold hover:bg-rose-500 transition shadow-lg flex items-center gap-2"
            >
                🛒 Shop Now
            </Link>
            <Link
                to="/personalize"
                className="px-2 py-3 border-2 border-rose-400 text-rose-300 rounded-lg font-semibold hover:bg-rose-900 hover:text-rose-100 transition flex items-center gap-2"
            >
                🎨 Personalize Gift
            </Link>
            <button
                onClick={() => navigate("/giftify-insights")}
                className="px-2 py-3 border-2 border-yellow-400 text-yellow-300 rounded-lg font-semibold hover:bg-yellow-900 hover:text-yellow-100 transition flex items-center gap-2"
            >
                ❓ Reason for Giftify
            </button>
            </div>
        </div>
        <div className="absolute inset-0 opacity-10">
          <img src="https://via.placeholder.com/1920x400/000000/FF1493?text=Gifts" alt="Gifting Banner" className="w-full h-full object-cover" />
        </div>
      </section>

      {/* Quick Links: Dark */}
      <div className="bg-gray-800/50 text-gray-300 text-xs py-3 border-b border-rose-800/30">
        <div className="max-w-6xl mx-auto px-4 flex flex-wrap justify-center gap-6">
          <Link to="/corporate" className="hover:text-rose-400 transition flex items-center gap-1">
            <FontAwesomeIcon icon={faShieldAlt} /> Corporate Gifting
          </Link>
          <Link to="/bulk" className="hover:text-rose-400 transition flex items-center gap-1">
            <FontAwesomeIcon icon={faTrophy} /> Bulk Orders
          </Link>
          <Link to="/wrapping" className="hover:text-rose-400 transition flex items-center gap-1">
            🎀 Gift Wrapping
          </Link>
          <Link to="/delivery" className="hover:text-rose-400 transition flex items-center gap-1">
            🚚 Same-Day Delivery
          </Link>
        </div>
      </div>

      {/* Filters & Search: Dark */}
      <div className="px-4 py-6 bg-gray-800">
        <div className="max-w-7xl mx-auto flex flex-col lg:flex-row gap-6">
          {/* Mobile Filters */}
          <div className="lg:hidden">
            <div className="flex gap-2 mb-4">
              <div className="relative flex-1">
                <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" />
                <input
                  type="text"
                  placeholder="Search gifts..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-gray-100 focus:outline-none focus:ring-2 focus:ring-rose-500 placeholder-gray-500"
                />
              </div>
              <button className="p-2 border w-10 border-gray-600 rounded-lg hover:bg-gray-700 text-gray-400">
                <FontAwesomeIcon icon={faFilter} />
              </button>
            </div>
            {/* Mobile Filter Dropdowns */}
            <select
              value={priceFilter}
              onChange={(e) => setPriceFilter(e.target.value)}
              className="w-full p-2 h-10 bg-gray-700 border border-gray-600 rounded-lg mb-2 text-gray-100"
            >
              <option value="all">All Prices</option>
              <option value="under20">Under ₹200</option>
              <option value="20-50">₹200 - ₹500</option>
              <option value="over50">Over ₹500</option>
            </select>
            <select
              value={ageFilter}
              onChange={(e) => setAgeFilter(e.target.value)}
              className="w-full p-2 h-10 bg-gray-700 border border-gray-600 rounded-lg mb-2 text-gray-100"
            >
              <option value="all">All Ages</option>
              <option value="Kids">Kids</option>
              <option value="Teens">Teens</option>
              <option value="Adults">Adults</option>
              <option value="Family">Family</option>
            </select>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="w-full p-2 h-10 bg-gray-700 border border-gray-600 rounded-lg text-gray-100"
            >
              <option value="popularity">Sort by Popularity</option>
              <option value="price">Sort by Price</option>
              <option value="new">Sort by New</option>
            </select>
          </div>

          {/* Desktop Sidebar Filters */}
          <div className="hidden lg:block w-64 bg-gray-700/50 p-4 rounded-lg">
            <h3 className="font-semibold mb-4 text-rose-400">Filters</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-300">Price Range</label>
                <select
                  value={priceFilter}
                  onChange={(e) => setPriceFilter(e.target.value)}
                  className="w-full p-2 h-10 bg-gray-600 border border-gray-500 rounded text-gray-100"
                >
                  <option value="all">All Prices</option>
                  <option value="under20">Under ₹200</option>
                  <option value="20-50">₹200 - ₹500</option>
                  <option value="over50">Over ₹500</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-300">Age Group</label>
                <select
                  value={ageFilter}
                  onChange={(e) => setAgeFilter(e.target.value)}
                  className="w-full p-2 h-10 bg-gray-600 border border-gray-500 rounded text-gray-100"
                >
                  <option value="all">All Ages</option>
                  <option value="Kids">Kids</option>
                  <option value="Teens">Teens</option>
                  <option value="Adults">Adults</option>
                  <option value="Family">Family</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-300">Sort By</label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="w-full p-2 h-10 bg-gray-600 border border-gray-500 rounded text-gray-100"
                >
                  <option value="popularity">Popularity</option>
                  <option value="price">Price Low to High</option>
                  <option value="new">Newest First</option>
                </select>
              </div>
              <div className="relative mb-4">
                <label className="block text-sm font-medium mb-2 text-gray-300">Search</label>
                <div className="relative">
                  <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" />
                  <input
                    type="text"
                    placeholder="Search gifts..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 bg-gray-600 border border-gray-500 rounded text-gray-100 focus:outline-none focus:ring-2 focus:ring-rose-500 placeholder-gray-500"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex-1">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-1 mb-2 place-items-center">
              {groupNames.map((group) => (
                <button
                  key={group.label}
                  onClick={() => setActiveTab(group.label)}
                  className={`flex flex-col items-center justify-center w-full px-3 py-3 rounded-lg text-xs font-medium transition-all backdrop-blur-sm shadow-md hover:scale-105 ${
                    activeTab === group.label
                      ? "bg-rose-700/80 text-rose-100 border-2 border-rose-400"
                      : "bg-gray-700/60 text-gray-200 hover:bg-rose-700/40 border border-gray-600"
                  }`}
                  title={group.description}
                  aria-pressed={activeTab === group.label}
                >
                  <FontAwesomeIcon icon={group.icon} className="text-lg mb-1" />
                  <span className="text-center leading-tight">{group.label}</span>
                </button>
              ))}
            </div>

            {/* Product Grid */}
            <div className={`grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6 place-items-center`}>
              {filteredGifts.length === 0 ? (
                <div className="col-span-full text-center text-gray-500 py-12">
                  <FontAwesomeIcon icon={faGift} className="text-6xl text-gray-400 mb-4 mx-auto" />
                  <p className="text-lg">No gifts found matching your criteria. Try adjusting filters!</p>
                  <button
                    onClick={() => { setSearchQuery(""); setPriceFilter("all"); setAgeFilter("all"); setSortBy("popularity"); }}
                    className="mt-4 px-6 py-2 bg-rose-600 text-gray-100 rounded-lg hover:bg-rose-700 transition"
                  >
                    Clear Filters
                  </button>
                </div>
              ) : (
                filteredGifts.map((gift) => (
                  <div
                    key={gift.id}
                    className="bg-gray-700 rounded-lg shadow-md overflow-hidden hover:shadow-xl transition-all duration-300 group"
                  >
                    <div className="relative overflow-hidden">
                      <img src={gift.image} alt={gift.name} className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300" />
                      {gift.isNew && <span className="absolute top-2 left-2 bg-green-600 text-gray-100 px-2 py-1 rounded text-xs font-bold">New!</span>}
                      {gift.isPersonalized && <span className="absolute top-2 right-2 bg-purple-600 text-gray-100 px-2 py-1 rounded text-xs font-bold">Personalized</span>}
                      {gift.isInnovative && <span className="absolute bottom-2 left-2 bg-blue-600 text-gray-100 px-2 py-1 rounded text-xs font-bold">Innovative</span>}
                    </div>
                    <div className="p-4">
                      <h3 className="font-semibold text-lg mb-2 leading-tight text-gray-100">{gift.name}</h3>
                      <p className="text-sm text-gray-400 mb-3 line-clamp-2">{gift.description}</p>
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-1">
                          <FontAwesomeIcon icon={faStar} className="text-yellow-400" />
                          <span className="text-sm">{gift.popularity}</span>
                        </div>
                        <span className="text-2xl font-bold text-rose-400">₹{gift.price}</span>
                      </div>
                      <div className="flex gap-2 mb-2">
                        <button
                          onClick={() => addToCart(gift, false)}
                          className="flex-1 bg-rose-600 text-gray-100 py-1 rounded-lg hover:bg-rose-700 transition font-medium flex items-center justify-center gap-1 text-sm"
                        >
                          <FontAwesomeIcon icon={faShoppingCart} /> Add to Cart
                        </button>
                        <button
                          onClick={() => addToCart(gift, true)}
                          className="flex-1 bg-purple-600 text-gray-100 py-1 rounded-lg hover:bg-purple-700 transition font-medium flex items-center justify-center gap-1 text-sm"
                        >
                          <FontAwesomeIcon icon={faGift} /> Wrap & Add
                        </button>
                      </div>
                      <button
                        onClick={() => toggleWishlist(gift)}
                        className={`w-full py-1 rounded-lg transition ${
                          wishlist.some(item => item.id === gift.id)
                            ? "bg-red-600 text-gray-100"
                            : "bg-gray-600 text-gray-300 hover:bg-gray-500"
                        }`}
                      >
                        {wishlist.some(item => item.id === gift.id) ? "❤️ In Wishlist" : "Add to Wishlist"}
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Load More */}
            {filteredGifts.length > 0 && (
              <div className="text-center mt-8">
                <button className="px-8 py-3 bg-gray-700 text-gray-200 rounded-lg hover:bg-rose-700 transition font-medium">
                  Load More Gifts
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Interactive Cart Sidebar/Modal */}
      <div className={`fixed inset-0 z-50 transition-transform duration-300 ${isCartOpen ? 'translate-x-0' : 'translate-x-full lg:translate-x-0 lg:opacity-0'} lg:relative lg:translate-x-0 lg:w-96 lg:ml-4 lg:mt-4`}>
        <div className={`h-full bg-gray-800 rounded-lg shadow-xl overflow-y-auto border border-rose-800/30 ${isCartOpen ? 'block' : 'hidden'} lg:block`}>
          <div className="p-4 border-b border-gray-700">
            <h3 className="font-semibold text-rose-400 text-lg flex items-center gap-2">
              <FontAwesomeIcon icon={faShoppingCart} /> Your Cart ({totalItems} items)
              <button onClick={() => setIsCartOpen(false)} className="ml-auto lg:hidden text-gray-400 hover:text-gray-200">
                ✕
              </button>
            </h3>
          </div>
          {cart.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <FontAwesomeIcon icon={faShoppingCart} className="text-4xl mb-4 mx-auto" />
              <p>Your cart is empty. Start shopping!</p>
            </div>
          ) : (
            <>
              {cart.map((item) => (
                <div key={item.id} className="p-4 border-b border-gray-700 last:border-b-0">
                  <div className="flex items-start gap-3">
                    <img src={item.image} alt={item.name} className="w-16 h-16 object-cover rounded" />
                    <div className="flex-1">
                      <p className="font-medium text-gray-100">{item.name}</p>
                      <p className="text-sm text-gray-400">${item.price}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <button onClick={() => updateQuantity(item.id, item.quantity - 1)} className="p-1 text-gray-500 hover:text-gray-200">
                          <FontAwesomeIcon icon={faMinus} />
                        </button>
                        <span className="px-3 py-1 bg-gray-600 rounded">{item.quantity}</span>
                        <button onClick={() => updateQuantity(item.id, item.quantity + 1)} className="p-1 text-gray-500 hover:text-gray-200">
                          <FontAwesomeIcon icon={faPlus} />
                        </button>
                      </div>
                      <div className="mt-2 flex items-center gap-2">
                        <input
                          type="checkbox"
                          id={`wrap-${item.id}`}
                          checked={item.giftWrap || false}
                          onChange={() => toggleGiftWrap(item.id)}
                          className="rounded"
                        />
                        <label htmlFor={`wrap-${item.id}`} className="text-sm text-gray-400 flex items-center gap-1">
                          <FontAwesomeIcon icon={faGift} /> Gift Wrap (+$5)
                        </label>
                      </div>
                      <div className="mt-2">
                        <input
                          type="text"
                          placeholder="Gift message..."
                          className="w-full p-2 bg-gray-700 border border-gray-600 rounded text-sm text-gray-100 placeholder-gray-500"
                        />
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <button onClick={() => toggleWishlist(item)} className="p-1 text-red-400 hover:text-red-300">
                        <FontAwesomeIcon icon={faHeart} />
                      </button>
                      <button onClick={() => removeFromCart(item.id)} className="p-1 text-red-500 hover:text-red-400">
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              <div className="p-4 space-y-3">
                {/* Coupon */}
                <div>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder="Enter coupon code"
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                      className="flex-1 p-2 bg-gray-700 border border-gray-600 rounded text-sm text-gray-100 placeholder-gray-500"
                    />
                    <button
                      onClick={applyCoupon}
                      className="px-4 py-2 bg-rose-600 text-gray-100 rounded hover:bg-rose-700 transition"
                    >
                      Apply
                    </button>
                  </div>
                  {appliedCoupon && (
                    <p className="text-sm text-green-400 mt-1 flex items-center gap-1">
                      <FontAwesomeIcon icon={faCheckCircle} /> {appliedCoupon.code} applied!
                      <button onClick={clearCoupon} className="ml-2 text-gray-400 hover:text-gray-200 text-xs">Remove</button>
                    </p>
                  )}
                </div>
                {/* Shipping Address */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Delivery Address</label>
                  <input
                    type="text"
                    placeholder="Enter delivery address"
                    value={shippingAddress}
                    onChange={(e) => setShippingAddress(e.target.value)}
                    className="w-full p-2 bg-gray-700 border border-gray-600 rounded text-sm text-gray-100 placeholder-gray-500"
                  />
                </div>
                {/* Totals */}
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">Subtotal: ${subtotal.toFixed(2)}</div>
                  <div className="flex justify-between">Wrapping: ${wrappingCost.toFixed(2)}</div>
                  <div className="flex justify-between">Shipping: ${shipping.toFixed(2)}</div>
                  <div className="flex justify-between">Tax (8%): ${tax.toFixed(2)}</div>
                  {discount > 0 && <div className="flex justify-between text-green-400">Discount: -${discount.toFixed(2)}</div>}
                  <div className="border-t border-gray-600 pt-2 font-semibold text-lg text-rose-400">
                    Total: ${total.toFixed(2)}
                  </div>
                </div>
                <div className="flex gap-2 pt-4">
                  <Link
                    to="/checkout"
                    className="flex-1 bg-rose-600 text-gray-100 py-3 rounded-lg text-center hover:bg-rose-700 transition font-medium"
                  >
                    Proceed to Checkout
                  </Link>
                  <button
                    onClick={() => { setCart([]); setAppliedCoupon(null); }}
                    className="flex-1 bg-gray-600 text-gray-200 py-3 rounded-lg hover:bg-gray-500 transition"
                  >
                    Clear Cart
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Footer: Dark */}
      <footer className="bg-gray-900/80 border-t border-rose-800/30 text-gray-400 py-8 mt-auto">
        <div className="max-w-6xl mx-auto px-4 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="text-sm text-center md:text-left">© 2025 Giftify - Spreading Joy One Gift at a Time. All rights reserved. 🎁</div>
          <div className="flex flex-wrap gap-6 text-sm justify-center md:justify-end">
            <Link to="/about" className="hover:text-rose-400 transition">About</Link>
            <Link to="/privacy" className="hover:text-rose-400 transition">Privacy</Link>
            <Link to="/terms" className="hover:text-rose-400 transition">Terms</Link>
            <Link to="/contact" className="hover:text-rose-400 transition">Contact</Link>
            <Link to="/help" className="hover:text-rose-400 transition">Help</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default GiftifyHome;